import React from 'react';
import { translate } from '../../helper';
import UserIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import PolicyIcon from '@mui/icons-material/Description';
import LanguageIcon from '@mui/icons-material/Language';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LogoutIcon from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import { Divider } from 'antd';

/**
 * UserProfileCard - Premium user profile section for dropdown
 * Displays avatar, name, and email with elegant styling
 */
const UserProfileCard = ({ userInfo }) => {
  const firstName = userInfo?.user?.first_name || userInfo?.first_name || '';
  const lastName = userInfo?.user?.last_name || userInfo?.last_name || '';
  const email = userInfo?.user?.email || userInfo?.email || '';
  const avatar = userInfo?.profile?.profile_image;
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || 'User';

  return (
    <div
      style={{
        padding: 'calc(var(--sp-lg) * 1.2) calc(var(--sp-md2)*1.3)',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)',
        borderRadius: '12px',
        marginBottom: 'calc(var(--sp-md2)*1.1)',
        display: 'flex',
        alignItems: 'center',
        gap: 'calc(var(--sp-md2)*1.1)',
        boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.5), 0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid rgba(26, 90, 120, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'default'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(255,255,255,0.5), 0 4px 12px rgba(26, 90, 120, 0.12)';
        e.currentTarget.style.background = 'linear-gradient(135deg, #f0f4f8 0%, #e8f0f7 100%)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'inset 0 1px 2px rgba(255,255,255,0.5), 0 1px 3px rgba(0,0,0,0.05)';
        e.currentTarget.style.background = 'linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)';
      }}>
      {/* Avatar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 'calc(var(--fs-xl)*2.2)',
          width: 'calc(var(--fs-xl)*2.2)',
          height: 'calc(var(--fs-xl)*2.2)',
          borderRadius: '50%',
          background: avatar ? 'transparent' : 'linear-gradient(135deg, #1a5a78 0%, #2a7a98 100%)',
          overflow: 'hidden',
          border: '2px solid rgba(26, 90, 120, 0.2)',
          boxShadow: '0 4px 12px rgba(26, 90, 120, 0.15)',
          flexShrink: 0
        }}>
        {avatar ? (
          <img
            src={avatar}
            alt="User avatar"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <UserIcon
            style={{
              fontSize: 'calc(var(--fs-xl)*1.2)',
              color: '#fff'
            }}
          />
        )}
      </div>

      {/* Name & Email */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'calc(var(--fs-xs) * 1.32075)',
            fontWeight: '700',
            color: '#1a5a78',
            letterSpacing: '-0.01em',
            marginBottom: 'calc(var(--sp-sm) * 0.6)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
          {fullName}
        </div>
        <div
          style={{
            fontSize: 'calc(var(--fs-xs) * 1.132075)',
            color: '#64748b',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '-0.005em',
            textTransform: 'lowercase'
          }}>
          {email}
        </div>
      </div>
    </div>
  );
};

/**
 * MenuItemWrapper - Reusable component for dropdown menu items
 * @param {Object} icon - Icon component to display
 * @param {string} label - Translation key for label
 * @param {string} color - Icon and text color
 */
const MenuItemWrapper = ({ icon: Icon, label, color = '#1a5a78' }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'calc(var(--sp-md2)*1.132075)',
      fontSize: 'calc(var(--fs-xs) * 1.32075)',
      fontWeight: '500',
      color: color === '#ff4d4f' ? color : '#2d3748',
      transition: 'all 0.2s ease'
    }}>
    <Icon
      style={{
        fontSize: 'calc(var(--fs-xs)*1.698113)',
        color: color,
        transition: 'all 0.2s ease'
      }}
    />
    {translate(label)}
  </div>
);

/**
 * TwoFactorToggleItem - Component for 2FA toggle menu item
 * @param {boolean} is2FAEnabled - Whether 2FA is currently enabled
 */
const TwoFactorToggleItem = ({ is2FAEnabled = false }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'calc(var(--sp-md2)*1.132075)',
      fontSize: 'calc(var(--fs-xs) * 1.32075)',
      fontWeight: '500',
      color: '#2d3748',
      transition: 'all 0.2s ease'
    }}>
    <SecurityIcon
      style={{
        fontSize: 'calc(var(--fs-xs)*1.698113)',
        color: '#1a5a78',
        transition: 'all 0.2s ease'
      }}
    />
    {is2FAEnabled ? translate('disable2FA') : translate('enable2FA')}
  </div>
);

/**
 * getDropdownMenuItems - Returns all dropdown menu items configuration
 * Includes the user profile card at the top
 * @param {Object} handlers - Object containing handler functions
 * @param {Object} userInfo - User information object
 * @param {Object} options - Additional options (e.g., is2FAEnabled)
 * @returns {Array} Array of menu item configurations
 */
export const getDropdownMenuItems = (handlers, userInfo, options = {}) => {
  const {
    onProfileClick,
    onPasswordClick,
    onPrivacyClick,
    onTermsClick,
    onLanguageClick,
    onSupportClick,
    onLogoutClick,
    on2FAClick
  } = handlers;

  const { is2FAEnabled = false } = options;

  return [
    {
      label: <UserProfileCard userInfo={userInfo} />,
      key: 'profile-card',
      disabled: true,
      style: { cursor: 'default' }
    },
    {
      type: 'divider',
      key: 'divider-1'
    },
    {
      label: <MenuItemWrapper icon={UserIcon} label="myProfile" />,
      key: '1',
      onClick: onProfileClick,
      className: 'item-dropdown-user menu-text'
    },
    {
      label: <MenuItemWrapper icon={LockIcon} label="changePassword" />,
      key: '2',
      onClick: onPasswordClick,
      className: 'item-dropdown-user menu-text'
    },
    {
      label: <TwoFactorToggleItem is2FAEnabled={is2FAEnabled} />,
      key: '2fa',
      onClick: on2FAClick,
      className: 'item-dropdown-user menu-text'
    },
    {
      label: <MenuItemWrapper icon={PrivacyTipOutlinedIcon} label="privacyPolicy" />,
      key: '3',
      onClick: onPrivacyClick,
      className: 'item-dropdown-user menu-text'
    },
    {
      label: <MenuItemWrapper icon={PolicyIcon} label="termsAndCondition" />,
      key: '4',
      onClick: onTermsClick,
      className: 'item-dropdown-user menu-text'
    },
    {
      label: <MenuItemWrapper icon={LanguageIcon} label="chooseLanguage" />,
      key: '5',
      onClick: onLanguageClick,
      className: 'item-dropdown-user menu-text'
    },
    {
      label: <MenuItemWrapper icon={SupportAgentIcon} label="support" />,
      key: '6',
      onClick: onSupportClick,
      className: 'item-dropdown-user menu-text'
    },
    {
      type: 'divider',
      key: 'divider-2'
    },
    {
      label: <MenuItemWrapper icon={LogoutIcon} label="logout" color="#ff4d4f" />,
      key: '7',
      onClick: onLogoutClick,
      className: 'item-dropdown-user menu-text'
    }
  ];
};
